import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/lib/supabase";
import { roleFromAccessToken } from "@/lib/jwt";
import { AuthLayout } from "@/components/Layout";
import { AuthCard } from "@/components/Card";
import { Button } from "@/components/Button";
import { FormField } from "@/components/FormField";
import { Alert } from "@/components/Alert";
import { Logo } from "@/components/Logo";

const schema = z
  .object({
    password: z.string().min(10, "At least 10 characters").max(128),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    message: "Passwords don't match",
    path: ["confirm"],
  });
type FormData = z.infer<typeof schema>;

export default function ResetPassword() {
  const nav = useNavigate();
  const [ready, setReady] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") setReady(true);
    });
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  async function onSubmit({ password }: FormData) {
    setServerError(null);
    const { error } = await supabase.auth.updateUser({ password });
    if (error) {
      setServerError(error.message);
      return;
    }
    // The recovery link signs the user in at aal1. Admins must still clear the
    // MFA gate, so route them through the same flow as a fresh admin sign-in
    // (challenge if they have a verified factor, otherwise enrollment).
    const { data: { session } } = await supabase.auth.getSession();
    if (roleFromAccessToken(session?.access_token) === "admin") {
      const factors = await supabase.auth.mfa.listFactors();
      const verifiedFactor = factors.data?.totp?.find((f) => f.status === "verified");
      nav(verifiedFactor ? "/admin/mfa/challenge" : "/admin/mfa/setup", { replace: true });
      return;
    }
    nav("/dashboard", { replace: true });
  }

  return (
    <AuthLayout>
      <AuthCard>
        <div className="mb-6 flex justify-center"><Logo size="lg" href="https://fortehealth.co" /></div>
        <h1 className="font-serif text-[25px] font-normal text-fh-ink text-center mb-1.5">
          Set a new password
        </h1>

        {!ready ? (
          <p className="text-fh-gray text-center">Verifying your link…</p>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-3.5 mt-4" noValidate>
            {serverError && <Alert tone="error">{serverError}</Alert>}
            <FormField
              label="New password"
              type="password"
              autoComplete="new-password"
              autoFocus
              hint="Minimum 10 characters."
              error={errors.password?.message}
              {...register("password")}
            />
            <FormField
              label="Confirm password"
              type="password"
              autoComplete="new-password"
              error={errors.confirm?.message}
              {...register("confirm")}
            />
            <Button type="submit" loading={isSubmitting} fullWidth className="mt-1">
              Update password
            </Button>
          </form>
        )}
      </AuthCard>
    </AuthLayout>
  );
}
